"use client";

import { useEffect, useMemo, useState } from "react";
import {
  useVisitorProfileQuery,
  type VisitorProfile,
} from "@/lib/graphql/queries/generated/profile.generated";
import type { VisitorIdentity } from "@/lib/use-widget-env";
import { pickDisplayName } from "../profile-tab/helpers";

const NO_VALUES: Record<string, string> = {};

/** name / email / phone the BE profile holds, in the shape the prechat coverage check reads. */
export function profileToIdentity(
  profile: VisitorProfile | undefined | null,
): Pick<VisitorIdentity, "name" | "email" | "phone"> | null {
  if (!profile) return null;
  const value = (key: string) =>
    profile.fields.find((f) => f.key === key)?.value?.trim() || undefined;
  return {
    name: pickDisplayName(profile, {}) || undefined,
    email: value("email"),
    phone: value("phone"),
  };
}

/** Every non-empty profile field value by key (custom pre-chat fields are matched on their key). */
export function profileFieldValues(
  profile: VisitorProfile | undefined | null,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const f of profile?.fields ?? []) {
    const v = f.value?.trim();
    if (v) out[f.key] = v;
  }
  return out;
}

/** A hung profile request must not hide the pre-chat for ever: after this long it counts as not pending. */
export const PROFILE_PENDING_CAP_MS = 3000;

/**
 * What a VERIFIED session knows about the visitor when the host passed no `user` prop (a session
 * restored from storage). The BE profile is the source: the persisted session payload only carries
 * `displayName`, never email / phone. `pending` is true while it loads so the prechat does not flash.
 */
export function useVerifiedIdentity(opts: { verified: boolean; enabled: boolean }): {
  identity: Pick<VisitorIdentity, "name" | "email" | "phone"> | null;
  /** Every non-empty profile value by field key. */
  fieldValues: Record<string, string>;
  pending: boolean;
} {
  const active = opts.verified && opts.enabled;
  const { data, loading } = useVisitorProfileQuery({ skip: !active });
  const identity = useMemo(
    () => (active ? profileToIdentity(data?.visitorProfile) : null),
    [active, data?.visitorProfile],
  );
  const fieldValues = useMemo(
    () => (active ? profileFieldValues(data?.visitorProfile) : NO_VALUES),
    [active, data?.visitorProfile],
  );
  const [capped, setCapped] = useState(false);
  const waiting = active && loading;
  useEffect(() => {
    if (!waiting) {
      setCapped(false);
      return;
    }
    const id = setTimeout(() => setCapped(true), PROFILE_PENDING_CAP_MS);
    return () => clearTimeout(id);
  }, [waiting]);
  return { identity, fieldValues, pending: waiting && !capped };
}
